import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, Mail, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '../../components/ui/toggle-group';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import RichTextEditor from '../../components/RichTextEditor';
import AdminTemplatesTable, { VisibilityBadge, useAdminTemplatesTable } from '../../components/admin/AdminTemplatesTable';
import { EMAIL_TEMPLATE_CATEGORIES } from '../../lib/emailTemplateCategories';
import { TEMPLATE_SCOPES, scopeCounts, sharingText } from '../../lib/adminTemplateScopes';
import { errorText } from '../../lib/apiErrors';
import useDisplayZone from '../../hooks/useDisplayZone';
import AdminLayout from './AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const LIBRARY_KEY = ['admin-email-templates'];
const EMPTY_FORM = { name: '', subject: '', body: '', category: 'general' };
const NO_FILTERS = { category: 'all', visibility: 'all', owner: 'all' };
// Stable fallbacks while a query has no data (loading or failed): a fresh `[]` each
// render gives TanStack Table new data on every render and it never settles.
const NO_ROWS = [];

/**
 * Admin → Email Templates (#978): the org's whole template library in one list.
 * A scope filter (All / Admin templates / Shared / Personal / Group) sits above
 * the category, visibility and owner filters. An admin edits or deletes any
 * template of their org from the row menu; on a member's template the edit is
 * content only and the owner later sees "Edited by". Create Template adds an
 * admin template, which every coach sees under Organisation.
 */
function EmailTemplatesLibrary() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    const [scope, setScope] = useState('all');
    const [filters, setFilters] = useState(NO_FILTERS);
    const [editing, setEditing] = useState(null);   // null = closed, {} = new, template = edit
    const [form, setForm] = useState(EMPTY_FORM);
    const [deleting, setDeleting] = useState(null);
    const zone = useDisplayZone();
    // Group names for the "Shared with <group>" lines. Only the groups list: the
    // members list `useShareTargets` also loads is not needed here.
    const { data: groups = NO_ROWS } = useQuery({
        queryKey: ['organization-groups'],
        queryFn: async () => {
            const res = await axios.get(`${API}/organizations/groups`, { headers: getAuthHeader() });
            return Array.isArray(res.data) ? res.data : [];
        },
    });

    const { data: templates = NO_ROWS, isLoading, error, refetch } = useQuery({
        queryKey: LIBRARY_KEY,
        queryFn: async () => {
            const res = await axios.get(`${API}/admin/email-templates/all`, { headers: getAuthHeader() });
            return Array.isArray(res.data) ? res.data : [];
        },
    });
    useEffect(() => {
        if (error) toast.error(errorText(error, 'Failed to load email templates'));
    }, [error]);

    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: LIBRARY_KEY });
        // Composers and Settings cache the template list; an admin edit must show there too.
        queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    };

    const save = useMutation({
        mutationFn: async ({ target, payload }) => {
            const headers = { headers: getAuthHeader() };
            return target?.template_id
                ? axios.put(`${API}/admin/email-templates/${target.template_id}`, payload, headers)
                : axios.post(`${API}/admin/email-templates`, payload, headers);
        },
        onSuccess: (_res, { target }) => {
            const owner = target && !target.is_admin_template ? target.owner_name : null;
            toast.success(target?.template_id
                ? (owner ? `Template updated. ${owner} will see that you edited it.` : 'Template updated')
                : 'Admin template created');
            setEditing(null);
            refresh();
        },
        onError: (err) => toast.error(errorText(err, 'Failed to save template')),
    });

    const remove = useMutation({
        mutationFn: (t) => axios.delete(`${API}/admin/email-templates/${t.template_id}`, { headers: getAuthHeader() }),
        onSuccess: () => {
            toast.success('Template deleted');
            setDeleting(null);
            refresh();
        },
        onError: (err) => toast.error(errorText(err, 'Failed to delete template')),
    });

    const openEdit = useCallback((t) => {
        setForm({ name: t.name || '', subject: t.subject || '', body: t.body || '', category: t.category || 'general' });
        setEditing(t);
    }, []);
    const openCreate = () => {
        setForm(EMPTY_FORM);
        setEditing({});
    };

    const handleSave = () => {
        if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) {
            toast.error('Please provide name, subject, and body');
            return;
        }
        save.mutate({ target: editing, payload: form });
    };

    const groupNames = useMemo(() => Object.fromEntries(groups.map((g) => [g.group_id, g.name])), [groups]);
    const owners = useMemo(() => {
        const seen = new Map();
        templates.forEach((t) => { if (!t.is_admin_template && t.user_id) seen.set(t.user_id, t.owner_name || 'Unknown'); });
        return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
    }, [templates]);
    const counts = useMemo(() => scopeCounts(templates), [templates]);

    const table = useAdminTemplatesTable({
        data: templates, scope, filters, groupNames, zone, onEdit: openEdit, onDelete: setDeleting,
    });
    const shown = table.getFilteredRowModel().rows.length;
    const setFilter = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));
    const clearFilters = () => { setScope('all'); setFilters(NO_FILTERS); };

    const isNew = editing && !editing.template_id;
    const isMemberTemplate = editing?.template_id && !editing.is_admin_template;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div data-testid="email-templates-library" className="space-y-4">
            <div className="flex items-center justify-between gap-3">
                <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    All templates
                </h3>
                <Button onClick={openCreate} className="bg-stone-900 hover:bg-stone-800" data-testid="create-email-template-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> Create Template
                </Button>
            </div>
            <p className="text-sm text-stone-500 -mt-2">
                Every template in your organisation in one list. Admin templates are the official set every
                coach can use; you can also fix any coach&apos;s template, and the coach sees that you edited it.
            </p>

            <div className="space-y-1.5">
                <ToggleGroup
                    type="single"
                    value={scope}
                    onValueChange={(v) => v && setScope(v)}
                    className="inline-flex flex-wrap justify-start rounded-lg border border-stone-200 bg-stone-100 p-0.5"
                    aria-label="Scope"
                    data-testid="template-scope-filter"
                >
                    {TEMPLATE_SCOPES.map((s) => (
                        <ToggleGroupItem
                            key={s.value}
                            value={s.value}
                            size="sm"
                            className="gap-1.5 data-[state=on]:bg-white data-[state=on]:shadow-sm"
                            data-testid={`scope-${s.value}`}
                        >
                            {s.label}
                            <span className="text-[11px] font-semibold px-1.5 rounded-full bg-stone-200/70 text-stone-600">
                                {counts[s.value]}
                            </span>
                        </ToggleGroupItem>
                    ))}
                </ToggleGroup>
                <p className="text-xs text-stone-500">{TEMPLATE_SCOPES.find((s) => s.value === scope)?.hint}</p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <Select value={filters.category} onValueChange={setFilter('category')}>
                    <SelectTrigger className="w-44" aria-label="Category" data-testid="category-filter-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All categories</SelectItem>
                        {EMAIL_TEMPLATE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={filters.visibility} onValueChange={setFilter('visibility')}>
                    <SelectTrigger className="w-40" aria-label="Visibility" data-testid="visibility-filter-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All visibilities</SelectItem>
                        <SelectItem value="private">Private</SelectItem>
                        <SelectItem value="shared">Shared</SelectItem>
                        <SelectItem value="organization">Org-wide</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={filters.owner} onValueChange={setFilter('owner')}>
                    <SelectTrigger className="w-44" aria-label="Owner" data-testid="owner-filter-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All owners</SelectItem>
                        <SelectItem value="admin">Admin templates</SelectItem>
                        {owners.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <span className="ml-auto text-xs text-stone-500" data-testid="template-count">
                    {shown} template{shown === 1 ? '' : 's'}
                </span>
            </div>

            {error ? (
                <div className="text-center py-12 border border-dashed border-stone-200 rounded-xl space-y-3" data-testid="templates-load-error">
                    <p className="text-stone-500">Couldn&apos;t load templates</p>
                    <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
                </div>
            ) : templates.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-stone-200 rounded-xl">
                    <Mail className="w-12 h-12 mx-auto text-stone-300 mb-3" />
                    <p className="text-stone-500 mb-3">No email templates yet</p>
                    <Button variant="outline" onClick={openCreate}>
                        <Plus className="w-4 h-4 mr-1" /> Create First Template
                    </Button>
                </div>
            ) : shown === 0 ? (
                <div className="text-center py-12 border border-dashed border-stone-200 rounded-xl space-y-3">
                    <p className="text-stone-500">No templates match these filters</p>
                    <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                        Clear filters
                    </Button>
                </div>
            ) : (
                <AdminTemplatesTable table={table} />
            )}

            <Dialog open={editing !== null} onOpenChange={(open) => { if (!open) setEditing(null); }}>
                <DialogContent className="sm:max-w-[880px] p-0 gap-0 flex flex-col max-h-[90vh] overflow-hidden">
                    <DialogHeader className="shrink-0 px-6 pt-6 pb-4 border-b border-stone-100">
                        <DialogTitle>
                            {isNew ? 'Create admin template' : isMemberTemplate ? 'Edit template' : 'Edit admin template'}
                        </DialogTitle>
                        <DialogDescription>
                            {isNew
                                ? 'Every coach in your organisation sees it under Organisation and in every composer.'
                                : isMemberTemplate
                                    ? `${editing.owner_name || 'A coach'}’s template · ${sharingText(editing, groupNames)}`
                                    : 'Admin template · org-wide'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-4">
                        {isMemberTemplate && (
                            <div className="flex gap-2.5 rounded-lg bg-stone-100 px-3.5 py-3 text-[13px] leading-relaxed text-stone-700" data-testid="admin-edit-notice">
                                <Info className="w-4 h-4 mt-0.5 shrink-0" />
                                <p>
                                    You are editing <strong>{editing.owner_name || 'a coach'}</strong>&apos;s template. You can change
                                    its name, category, subject and body. Who owns it and who it is shared with stay as the owner set
                                    them, and they will see that you edited it.
                                </p>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="admin-template-name">Template Name</Label>
                                <Input
                                    id="admin-template-name"
                                    value={form.name}
                                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                                    placeholder="e.g., Initial Outreach, Follow-up #1..."
                                    className="mt-1"
                                    data-testid="email-template-name-input"
                                />
                            </div>
                            <div>
                                <Label>Category</Label>
                                <Select value={form.category} onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}>
                                    <SelectTrigger className="mt-1" aria-label="Template category" data-testid="email-template-category-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {EMAIL_TEMPLATE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="admin-template-subject">Subject Line</Label>
                            <Input
                                id="admin-template-subject"
                                value={form.subject}
                                onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
                                placeholder="e.g., Quick question about {{Company}}..."
                                className="mt-1"
                                data-testid="email-template-subject-input"
                            />
                            <p className="text-xs text-stone-500 mt-1">
                                Use {'{{FirstName}}'}, {'{{Company}}'}, {'{{Title}}'} for personalization
                            </p>
                        </div>
                        <div>
                            <Label>Email Body</Label>
                            <div className="mt-1" data-testid="email-template-body-editor">
                                <RichTextEditor
                                    content={form.body}
                                    onChange={(html) => setForm((p) => ({ ...p, body: html }))}
                                    placeholder="Write your email template here..."
                                    minHeight="200px"
                                    showMergeVariables
                                    showAIWriter
                                    showContentLibrary
                                    onSubjectGenerated={(subj) => setForm((p) => ({ ...p, subject: subj }))}
                                />
                            </div>
                        </div>
                        {isMemberTemplate && (
                            <div>
                                <Label>Visibility and sharing</Label>
                                <div className="mt-1 flex items-center gap-2 min-h-9 rounded-md border border-stone-200 bg-stone-50 px-3 text-sm text-stone-600">
                                    <VisibilityBadge visibility={editing.visibility || 'private'} />
                                    <span>{sharingText(editing, groupNames)}</span>
                                    <span className="ml-auto text-xs text-stone-500">Only the owner can change this</span>
                                </div>
                            </div>
                        )}
                        {!isMemberTemplate && (
                            <p className="text-xs text-stone-500">
                                Admin templates are org-wide: every coach sees them under Organisation and in every composer,
                                and can make a copy to edit.
                            </p>
                        )}
                    </div>

                    <DialogFooter className="shrink-0 px-6 py-4 border-t border-stone-100">
                        <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                        <Button
                            onClick={handleSave}
                            disabled={save.isPending}
                            className="bg-stone-900 hover:bg-stone-800"
                            data-testid="save-email-template-btn"
                        >
                            {save.isPending ? 'Saving...' : isNew ? 'Create Template' : 'Save changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={deleting !== null} onOpenChange={(open) => { if (!open) setDeleting(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete “{deleting?.name}”?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleting?.is_admin_template
                                ? 'This admin template disappears from every coach’s composer. Copies coaches already made are not affected. This cannot be undone.'
                                : `This template belongs to ${deleting?.owner_name || 'a coach'}. It is removed for them and everyone it is shared with. This cannot be undone.`}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-red-600 hover:bg-red-700"
                            onClick={(e) => { e.preventDefault(); remove.mutate(deleting); }}
                            disabled={remove.isPending}
                            data-testid="confirm-delete-template-btn"
                        >
                            Delete template
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default function AdminEmailTemplatesPage() {
    return (
        <AdminLayout title="Email Templates" description="Create and manage admin and team email templates">
            <EmailTemplatesLibrary />
        </AdminLayout>
    );
}
